/**
 * Список ПЗ за день однією картинкою — для чергового, який кидає її в чат.
 *
 * Малюємо самі на canvas, а не знімаємо DOM: скло з backdrop-filter жоден
 * растеризатор не відтворює, а картка в чаті має читатися
 * на будь-якому тлі. Кольори беремо з теми, тож картинка збігається з
 * тим, що бачить діловод: градієнтне тло з плямами акценту, знак «Рапорт+»
 * зверху, під ним — плитка на кожен прийом їжі.
 */

export interface MealTile {
  title: string;
  total: number;
  reports: number;
  blocks: Array<{ unit: string; people: string[] }>;
}

const FONT = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, Arial, sans-serif";

/** Прямокутник плюса в картинці слова — ті самі частки, що в .brand-logo. */
const PLUS = { x: 0.80996, y: 0.04152, w: 0.17904, h: 0.58131 };

/** Колір токена теми, розв'язаний до rgb: canvas не розуміє color-mix(). */
function themeColor(token: string, fallback: string): string {
  const probe = document.createElement('span');
  probe.style.color = `var(${token})`;
  probe.style.display = 'none';
  document.body.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  return value && value !== 'rgba(0, 0, 0, 0)' ? value : fallback;
}

/** rgb(...) → rgba(..., a): для плям акценту й скла картки. */
function withAlpha(color: string, alpha: number): string {
  const m = color.match(/[\d.]+/g);
  if (!m || m.length < 3) return color;
  return `rgba(${m[0]}, ${m[1]}, ${m[2]}, ${alpha})`;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    // Без знака картинка все одно має вийти — чат чекає на список, не на логотип.
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

interface Palette {
  bg: string;
  panel: string;
  text: string;
  muted: string;
  accent: string;
  accentSoft: string;
  line: string;
  light: boolean;
}

const PAD = 28;
const GAP = 28;
const LINE_H = 24;
const COL_MIN = 200;
const TILE_HEAD = 22 + 22;

/** Скільки стовпчиків і якої ширини потрібно плитці. */
function tileColumns(ctx: CanvasRenderingContext2D, meal: MealTile): { cols: number; colW: number } {
  const cols = Math.max(1, Math.min(3, meal.blocks.length));
  ctx.font = `15px ${FONT}`;
  let colW = COL_MIN;
  for (const b of meal.blocks) {
    for (const [i, p] of b.people.entries()) {
      colW = Math.max(colW, ctx.measureText(`${i + 1}. ${p}`).width + 40);
    }
  }
  return { cols, colW };
}

const blockHeight = (people: number): number => 34 + 10 + people * LINE_H;

function tileRows(meal: MealTile, cols: number): number[] {
  const rows = Math.ceil(meal.blocks.length / cols) || 1;
  const out: number[] = [];
  for (let r = 0; r < rows; r++) {
    const slice = meal.blocks.slice(r * cols, r * cols + cols);
    out.push(Math.max(...slice.map(b => blockHeight(b.people.length)), 0));
  }
  return out;
}

function tileHeight(meal: MealTile, cols: number): number {
  return PAD + TILE_HEAD + tileRows(meal, cols).reduce((a, b) => a + b + GAP, 0) - GAP + PAD;
}

/** Одна плитка прийому їжі: картка з заголовком, підсумком і групами. */
function drawTile(
  ctx: CanvasRenderingContext2D,
  meal: MealTile,
  x0: number,
  y0: number,
  width: number,
  cols: number,
  pal: Palette,
  background: HTMLCanvasElement,
): number {
  const height = tileHeight(meal, cols);
  const colW = (width - PAD * 2 - (cols - 1) * GAP) / cols;

  // Розмиваємо лише знімок фону; текст малюється після скла.
  ctx.save();
  roundRect(ctx, x0, y0, width, height, 18);
  ctx.clip();
  ctx.filter = 'blur(16px)';
  ctx.drawImage(background, 0, 0, background.width / 2, background.height / 2);
  ctx.filter = 'none';
  ctx.fillStyle = pal.light ? 'rgba(255,255,255,0.6)' : 'rgba(18,18,20,0.58)';
  ctx.fillRect(x0, y0, width, height);
  // Внутрішній відблиск як --glass-shine: 2px, без зовнішньої рамки.
  const edge = ctx.createLinearGradient(x0, y0, x0 + width, y0 + height);
  edge.addColorStop(0, pal.light ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)');
  edge.addColorStop(1, pal.light ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.12)');
  ctx.strokeStyle = edge;
  ctx.filter = 'blur(0.5px)';
  ctx.lineWidth = 2;
  roundRect(ctx, x0 + 1, y0 + 1, width - 2, height - 2, 17);
  ctx.stroke();
  ctx.restore();

  // Один рядок: «Сніданок: 29 / 34к» — скільки їсть і скільки рапортів
  // (компенсацій). Дата стоїть у шапці картинки, у плитці вона зайва.
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = pal.text;
  ctx.font = `600 22px ${FONT}`;
  const name = meal.title.replace(/\s+\d{2}\.\d{2}\.\d{4}\s*$/, '');
  const ty = y0 + PAD + 22;
  ctx.fillText(`${name}: ${meal.total}`, x0 + PAD, ty);
  let x = x0 + PAD + ctx.measureText(`${name}: ${meal.total}`).width;
  ctx.fillStyle = pal.muted;
  ctx.font = `500 22px ${FONT}`;
  ctx.fillText(` / ${meal.reports}к`, x, ty);

  let y = y0 + PAD + TILE_HEAD;
  const rows = tileRows(meal, cols);
  for (let r = 0; r < rows.length; r++) {
    for (let c = 0; c < cols; c++) {
      const b = meal.blocks[r * cols + c];
      if (!b) continue;
      const bx = x0 + PAD + c * (colW + GAP);
      const label = `${b.unit} — ${b.people.length}`;
      ctx.font = `600 14px ${FONT}`;
      const lw = ctx.measureText(label).width + 24;
      ctx.fillStyle = pal.accentSoft;
      roundRect(ctx, bx, y, lw, 28, 8);
      ctx.fill();
      ctx.fillStyle = pal.accent;
      ctx.fillText(label, bx + 12, y + 19);
      ctx.font = `15px ${FONT}`;
      ctx.fillStyle = pal.text;
      let py = y + 34 + 10 + 17;
      for (const [i, p] of b.people.entries()) {
        ctx.textAlign = 'right';
        ctx.fillText(`${i + 1}.`, bx + 30, py);
        ctx.textAlign = 'left';
        ctx.fillText(p, bx + 36, py);
        py += LINE_H;
      }
    }
    y += rows[r]! + GAP;
  }
  return height;
}

/** Намалювати список за день і повернути PNG. */
export async function renderDayImage(dateLabel: string, meals: MealTile[]): Promise<Blob> {
  const light = document.documentElement.dataset.theme === 'light';
  const pal: Palette = {
    bg: themeColor('--bg', light ? '#f5f5f7' : '#000000'),
    panel: themeColor('--panel', light ? '#ffffff' : '#1d1d1f'),
    text: themeColor('--text', light ? '#1d1d1f' : '#f5f5f7'),
    muted: themeColor('--muted', '#86868b'),
    accent: themeColor('--accent', '#ff9000'),
    accentSoft: themeColor('--accent-soft', light ? '#fdeedb' : '#3a2a10'),
    line: themeColor('--line', light ? '#d2d2d7' : '#2c2c2e'),
    light,
  };
  const [word, plus] = await Promise.all([
    loadImage(light ? '/brand-word-light.png?v=5' : '/brand-word.png?v=5'),
    loadImage('/brand-plus.png?v=5'),
  ]);

  const scale = 2;
  const probe = document.createElement('canvas').getContext('2d')!;
  const layout = meals.map(m => tileColumns(probe, m));
  // Ширина картинки — за найширшою плиткою, але не вужча за 760.
  const width = Math.max(
    760,
    ...layout.map(l => PAD * 2 + l.cols * l.colW + (l.cols - 1) * GAP + PAD * 2),
  );
  const logoH = 40;
  const headH = PAD + logoH + PAD;
  const tilesH = meals.reduce((a, m, i) => a + tileHeight(m, layout[i]!.cols) + GAP, 0) - GAP;
  const height = headH + tilesH + PAD;

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);

  // Тло: рівний колір теми, поверх — вертикальний градієнт і плями акценту,
  // як у застосунку.
  ctx.fillStyle = pal.bg;
  ctx.fillRect(0, 0, width, height);
  const vert = ctx.createLinearGradient(0, 0, 0, height);
  vert.addColorStop(0, withAlpha(pal.accent, light ? 0.16 : 0.22));
  vert.addColorStop(0.45, withAlpha(pal.accent, 0));
  vert.addColorStop(1, withAlpha(pal.accent, light ? 0.08 : 0.1));
  ctx.fillStyle = vert;
  ctx.fillRect(0, 0, width, height);
  const glow = (cx: number, cy: number, r: number, a: number): void => {
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, withAlpha(pal.accent, a));
    g.addColorStop(1, withAlpha(pal.accent, 0));
    ctx.fillStyle = g;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  };
  glow(width * 0.12, height * 0.08, Math.max(320, width * 0.5), light ? 0.22 : 0.28);
  glow(width * 0.92, height * 0.55, Math.max(280, width * 0.4), light ? 0.14 : 0.18);
  glow(width * 0.35, height * 0.98, Math.max(240, width * 0.35), light ? 0.1 : 0.14);

  // Статичний кадр ASCII-хвилі: ті самі клітинки 14px і символи, що у фоні UI.
  ctx.save();
  ctx.font = '14px ui-monospace, Menlo, Consolas, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = pal.text;
  const glyphs = ['.·`,', ':;-~', '=+*^', '#%&$', '@WM8'];
  for (let j = 0; j < height / 14; j++) {
    for (let i = 0; i < width / 14; i++) {
      const v = ((Math.sin(i * 0.045 + 1.08) + Math.sin(j * 0.06 - 0.84) +
        Math.sin((i + j) * 0.03 + 0.6) + Math.sin(Math.hypot(i - width / 28, j - height / 28) * 0.12 - 1.44) + 4) / 8) ** 3;
      const k = Math.floor(v * 5.3) - 1;
      if (k < 0) continue;
      const chars = glyphs[Math.min(k, 4)]!;
      const seed = (Math.imul(i, 73856093) ^ Math.imul(j, 19349663)) >>> 0;
      ctx.globalAlpha = Math.max(1, Math.round(v * 6)) / 6 * 0.3;
      ctx.fillText(chars[seed % 251 % chars.length]!, i * 14 + 7, j * 14 + 7);
    }
  }
  ctx.restore();
  const background = document.createElement('canvas');
  background.width = canvas.width;
  background.height = canvas.height;
  background.getContext('2d')!.drawImage(canvas, 0, 0);

  // Знак «Рапорт+» зверху ліворуч і дата праворуч.
  if (word) {
    const w = (word.width / word.height) * logoH;
    const lx = PAD;
    const ly = PAD;
    ctx.save();
    // Слово — біле на чорному: screen на темній темі лишає лише напис;
    // світле слово — чорне на білому, тому multiply.
    ctx.globalCompositeOperation = light ? 'multiply' : 'screen';
    ctx.drawImage(word, lx, ly, w, logoH);
    ctx.restore();
    if (plus) {
      ctx.drawImage(plus, lx + w * PLUS.x, ly + logoH * PLUS.y, w * PLUS.w, logoH * PLUS.h);
    }
  }
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = pal.muted;
  ctx.font = `600 16px ${FONT}`;
  ctx.fillText(`Список ПЗ на ${dateLabel}`, width - PAD, PAD + logoH / 2 + 6);

  let y = headH;
  for (const [i, m] of meals.entries()) {
    y += drawTile(ctx, m, PAD, y, width - PAD * 2, layout[i]!.cols, pal, background) + GAP;
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Не вдалося зібрати картинку'))), 'image/png');
  });
}

/**
 * Скопіювати список за день у буфер обміну картинкою.
 *
 * ClipboardItem отримує проміс, а не готовий blob: WebKit приймає запис у
 * буфер лише всередині жесту користувача, і поки картинка малюється, жест
 * скінчився б. З промісом дозвіл береться одразу, а дані підтягуються потім.
 * Буфер закритий (http без TLS, старий WebKit, заборона в налаштуваннях) —
 * картинка йде файлом, щоб черговий не лишився ні з чим.
 */
export async function copyDayImage(dateLabel: string, meals: MealTile[]): Promise<'copied' | 'saved'> {
  const png = renderDayImage(dateLabel, meals);
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      return 'copied';
    } catch {
      /* нижче — файлом */
    }
  }
  const blob = await png;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Список ПЗ ${dateLabel}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return 'saved';
}
